import { Input, Button, Typography } from "@material-tailwind/react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";

import service from "../../service";

export type LoginForm = {
  captcha: string;
  username: string;
  password: string;
};

const Login = () => {
  const queryClient = useQueryClient();
  const { register, handleSubmit, setError, formState, reset } =
    useForm<LoginForm>();
  const clientSignature = useQuery({
    queryKey: ["clientSignature"],
    queryFn: service.clientSignature,
  });

  const login = useMutation(service.login, {
    onSuccess: async (res) => {
      window?.localStorage.setItem("auth", res.data.data.token);
      const profile = await service.getProfile();
      queryClient.setQueryData(["profile"], profile.data);
      navigate("/");
    },
    onError: (error: AxiosError<{ code: number; msg: string }>) => {
      switch (error?.response?.data?.code) {
        case 10001:
          setError("root", { message: error?.response?.data?.msg || "" });
          break;
        case 10002:
          setError("captcha", { message: error?.response?.data?.msg || "" });
          break;
        default:
          break;
      }
      return;
    },
  });

  const navigate = useNavigate();
  return (
    <div className="login-page relative flex min-h-screen items-center justify-center overflow-hidden bg-blue-gray-900 px-4 py-8 sm:px-6">
      <video
        className="login-page__video"
        playsInline
        autoPlay
        muted
        loop
        aria-hidden="true"
      >
        <source
          src={`${process.env.PUBLIC_URL}/video/football.mp4`}
          type="video/mp4"
        />
      </video>
      <div
        className="absolute inset-0 z-0 bg-blue-gray-900/50"
        aria-hidden="true"
      />

      <section className="relative z-10 max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-3xl border border-white/30 bg-white/95 p-5 text-center shadow-2xl shadow-blue-gray-900/30 backdrop-blur sm:p-8">
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-green-50 p-2">
          <img
            src="/logo192.png"
            className="h-full w-full object-contain"
            alt=""
            height={56}
            width={56}
          />
        </div>
        <h1 className="mb-2 text-2xl font-bold tracking-tight text-blue-gray-900 sm:text-3xl">
          Sportbook Backoffice
        </h1>
        <p className="mb-6 text-sm text-blue-gray-500">Sign in to continue</p>
        <form
          className="flex flex-col gap-y-4"
          onSubmit={handleSubmit(async (formData) => {
            reset();
            login.mutate({
              password: formData.password,
              username: formData.username,
              captcha: formData.captcha,
              clientSignature: clientSignature.data?.result.uuid || "",
            });
          })}
        >
          <Input
            className="bg-white text-blue-gray-900"
            crossOrigin=""
            label="Username"
            error={Boolean(formState.errors.root)}
            {...register("username")}
          />
          <Input
            className="bg-white text-blue-gray-900"
            crossOrigin=""
            label="Password"
            type="password"
            error={Boolean(formState.errors.root)}
            {...register("password")}
          />
          {Boolean(formState.errors.root) && (
            <Typography variant="small" color="red">
              {formState.errors.root?.message}
            </Typography>
          )}

          {!!clientSignature.data?.result?.captchaUrl && (
            <>
              <img
                className="mx-auto my-1 h-auto w-full max-w-[150px]"
                src={clientSignature.data?.result?.captchaUrl}
                alt=""
                width={150}
                height={50}
              />
              <Input
                className="bg-white text-blue-gray-900 !border-t-blue-gray-300 focus:!border-t-green-700"
                crossOrigin=""
                placeholder="Please type the captcha text"
                labelProps={{
                  className: "before:content-none after:content-none",
                }}
                containerProps={{
                  className: "min-w-0",
                }}
                {...register("captcha")}
              />
              {Boolean(formState.errors.captcha) && (
                <Typography variant="small" color="red">
                  {formState.errors.captcha?.message}
                </Typography>
              )}
            </>
          )}

          <Button
            type="submit"
            color="green"
            className="min-h-11 rounded-xl bg-green-700 text-sm font-semibold shadow-md shadow-green-900/20 hover:bg-green-800"
          >
            Log in
          </Button>
          <div className="pt-2 text-xs leading-5 text-blue-gray-500 sm:text-sm">
            <p>Please contact your administrator if you need help signing in.</p>
          </div>
        </form>
      </section>
    </div>
  );
};

export default Login;
